<template>
  <div class="modal-backdrop">
    <div class="payment-modal">

      <!-- HEADER -->
      <div class="modal-header">
        <span>Net Bill Amount = {{ grandTotal }}</span>
        <button class="cancel-btn" @click="onClose">
          ✕
        </button>
      </div>

      <!-- BODY -->
      <div class="modal-body">

        <!-- RIGHT CARD -->
        <div class="panel card right-panel">
          <div class="panel-title">Mode of payment</div> 
          <div class="row">
            <div class="col-lg-8">
              <v-select
                  placeholder="Select Cash & Bank Account"
                  class="w-100"
                  v-model="formData.mop_account_head_id"
                  name="mop_account_head_id"
                  :options="cashAndBank"
                  label="name"
                  :reduce="name => name.id"
              />
            </div>
            <div class="col-lg-4">
              <vField
                v-model="formData.payment"
                name="amount"
                type="number"
                class="form-control text-right"
                @keyup="validatePaymentAmount"
              />
            </div>
          </div>   

          <div class="payment-row split">
            <div>
              <label>Cash Received</label>
              <vField
                v-model="receivedAmount"
                name="amount"
                type="number"
                class="form-control text-right"
              />
            </div>
            <div>
              <label>Change Returned</label>
              <input type="number" class="form-control text-right" :value="changeAmount" readonly />
            </div>
          </div>      

          <div class="row mt-auto">
            <div class="col-lg-6"></div>
            <div class="col-lg-6">
              <div class="d-flex justify-content-end mt-2">
                <button @click="emit('onSubmit')" class="btn btn-primary" :disabled="submitLoading">
                  <span v-if="submitLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  {{ submitLabel }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, ref, inject, onMounted, watch } from 'vue';
import {useRoute, useRouter} from 'vue-router'
import handleContact from '@/services/modules/contact';
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";

const emit = defineEmits(['close', 'onSubmit']);
const formData = inject('formData');

const { fetchContactProfiles, fetchSingleContactProfile } = handleContact();
const {formatPatientLabel} = useAsyncDropdownHelper();

const props = defineProps({
  grandTotal: Number,
  rewardPointsAccountHeads: Array,
  cashAndBank: Array,
  submitLabel: {
    type: String,
    default: 'PRINT'
  },
  submitLoading: {
    type: Boolean,
    default: false
  }
})

const route = useRoute();
const router = useRouter();

const receivedAmount = ref(0)
const selectedData = ref({});

const changeAmount = computed(() => {
    if (formData.payment > 0 && receivedAmount.value && receivedAmount.value > 0) {
        return receivedAmount.value - formData.payment;
    }
    return 0
})

const netPayable = computed(() => {
  const total = Number(props.grandTotal) || 0;
  const redeemed = Number(formData.points_redeemed) || 0;
  return total - redeemed;
});

const onClose = () => {
  emit('close');
}
const validatePaymentAmount = (event) => {
    const amount = event.target.value
    if (amount < 0) {
      return showError("Payment Amount cannot be less than 0")
    }

    if (amount > netPayable.value) {
        formData.payment = netPayable.value
    }
}

watch(netPayable, () => {
  formData.payment = netPayable.value;
})

onMounted(async () => {
  formData.payment = netPayable.value;
})
</script>

<style scoped>
/* BACKDROP */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* MODAL */
.payment-modal {
  width: 70%;
  background: #fff;
  font-family: Arial, sans-serif;
  border-radius: 14px;
  overflow: hidden;
}

/* HEADER */
.modal-header {
  position: relative;
  background: #0b5394;
  color: #fff;
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* CANCEL BUTTON */
.cancel-btn {
  position: absolute;
  right: 16px;
  top: 10px;
  background: transparent;
  border: none;
  font-size: 26px;
  color: #fff;
  cursor: pointer;
}

/* BODY */
.modal-body {
  display: flex;
  gap: 20px;
  padding: 20px;
  background: #f4f6f9;
}

/* PANELS AS CARDS */
.left-panel,
.right-panel {
  background: #ffffff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.right-panel {
  width: 60%;
  display: flex;
  flex-direction: column;
}

/* LEFT PANEL ROWS */
.panel-row {
  border-bottom: 1px solid #ddd;
  padding: 12px 6px;
  min-height: 55px;
}

.panel-row:last-child {
  border-bottom: none;
}

.panel-row.title {
  font-weight: bold;
}

.panel-row.muted {
  background: #f2f2f2;
  border-radius: 8px;
}

/* RIGHT PANEL */
.panel-title {
  font-weight: bold;
  margin-bottom: 10px;
}

/* PAYMENT ROWS */
.payment-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 6px;
  border-bottom: 1px solid #ddd;
}

.payment-row.split {
  gap: 20px;
}

.payment-row.split div {
  flex: 1;
}

.payment-row label {
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
}

.payment-row input {
  width: 100%;
  padding: 6px;
  border: 1px solid #aaa;
  border-radius: 6px;
}

/* AMOUNT */
.amount {
  font-weight: bold;
}

/* ACTION BUTTONS */
.action-row {
  margin-top: auto;
  display: flex;
  border-radius: 12px;
  overflow: hidden;
}

/* LINKS */
.link {
  color: #000;
  font-weight: bold;
}
</style>
